
const STORAGE_PREFIX = 'ae.experimentalFormLayout'
const SECTION_LAZY_ROOT_MARGIN = '900px 0px 1200px 0px'
const SECTION_IDLE_RENDER_TIMEOUT = 1200
const SECTION_EAGER_RENDER_LIMIT = 4
const SECTION_EAGER_CONTROL_LIMIT = 100

const ExperimentalFormSection = {
  name: 'ae-experimental-form-section',
  inject: {
    consumeLazyEagerSection: {
      default: null,
    },
    registerLazySection: {
      default: null,
    },
    unregisterLazySection: {
      default: null,
    },
    requestLazySectionRender: {
      default: null,
    },
  },
  props: {
    section: Object,
    document: Object,
    form: Object,
    theme: Object,
    readonly: {
      type: Boolean,
      default: false,
    },
    path: {
      type: Array,
      default: () => [],
    },
    sticky: {
      type: Boolean,
      default: true,
    },
    activeStickySectionKey: {
      type: [String, Number],
      default: null,
    },
    fieldValidationErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      bodyRendered: false,
      bodyObserver: null,
    }
  },
  created() {
    if (this.consumeLazyEagerSection?.(this.section)) {
      this.bodyRendered = true
    }
  },
  computed: {
    sectionKey() {
      return this.section?.key ?? this.section?.index
    },
    headerPath() {
      return this.path.filter(Boolean).join(' / ')
    },
    isActiveStickyHeader() {
      return this.sticky && String(this.activeStickySectionKey) === String(this.sectionKey)
    },
    isCollapsible() {
      return this.section?.tags?.collapsible === true || this.section?.style?.collapsible === true
    },
    paragraphItemStyle() {
      return {
        minWidth: 0,
      }
    },
    sectionLayoutId() {
      return this.theme?.sections?.levels?.[this.section?.level]?.layoutId ?? '4dfdf094-32b3-41fe-a2eb-50a809c39d7d'
    },
    shouldRenderBody() {
      return this.bodyRendered
    },
  },
  mounted() {
    this.registerLazySection?.(this)
    this.observeSectionBody()
  },
  updated() {
    this.renderBodyIfNearViewport()
  },
  beforeDestroy() {
    this.disconnectBodyObserver()
    this.unregisterLazySection?.(this)
  },
  methods: {
    renderBody() {
      if (this.bodyRendered) {
        return
      }

      this.bodyRendered = true
      this.disconnectBodyObserver()
    },
    observeSectionBody() {
      if (this.bodyRendered || typeof IntersectionObserver === 'undefined' || !this.$el) {
        this.requestLazySectionRender?.(this)
        return
      }

      const root = this.resolveLazyScrollRoot()
      if (this.renderBodyIfNearViewport(root)) {
        return
      }

      this.bodyObserver = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.renderBody()
        }
      }, {
        root,
        rootMargin: SECTION_LAZY_ROOT_MARGIN,
        threshold: 0,
      })

      this.bodyObserver.observe(this.$el)
      this.requestFrame(() => this.renderBodyIfNearViewport(root))
    },
    resolveLazyScrollRoot() {
      let node = this.$el?.parentElement ?? this.$el ?? null
      while (node && node !== document.body && node !== document.documentElement) {
        if (this.isLazyScrollRootCandidate(node)) {
          return node
        }

        node = node.parentElement
      }

      return document.scrollingElement ?? document.documentElement
    },
    isLazyScrollRootCandidate(node) {
      if (!node?.getBoundingClientRect) {
        return false
      }

      const className = String(node.className ?? '')
      const hasScrollMarker = node.hasAttribute?.('data-ae-form-scroll-container')
        || className.includes('-form-scroll')
        || className.includes('ae-right-pane')
        || className.includes('form-content-pane')
      let overflow = ''
      try {
        const style = getComputedStyle(node)
        overflow = (style.overflowY || style.overflow || '').toLowerCase()
      } catch {
        overflow = ''
      }

      const allowsScroll = /auto|scroll|overlay/.test(overflow)
      const canScroll = ((node.scrollHeight ?? 0) - (node.clientHeight ?? 0)) > 2
      return (hasScrollMarker && (allowsScroll || canScroll)) || (allowsScroll && canScroll)
    },
    renderBodyIfNearViewport(root = null) {
      if (this.bodyRendered || !this.$el?.getBoundingClientRect) {
        return false
      }

      const box = this.$el.getBoundingClientRect()
      const rootBox = root?.getBoundingClientRect?.()
      const top = rootBox?.top ?? 0
      const bottom = rootBox?.bottom ?? window.innerHeight
      const nearTop = top - 900
      const nearBottom = bottom + 1200
      if (box.bottom >= nearTop && box.top <= nearBottom) {
        this.renderBody()
        return true
      }

      return false
    },
    disconnectBodyObserver() {
      if (this.bodyObserver) {
        this.bodyObserver.disconnect()
        this.bodyObserver = null
      }
    },
    requestFrame(callback) {
      if (typeof requestAnimationFrame === 'function') {
        return requestAnimationFrame(callback)
      }

      return setTimeout(() => callback(Date.now()), 0)
    },
    cancelFrame(frame) {
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frame)
      } else {
        clearTimeout(frame)
      }
    },
    subsectionPath(section) {
      return [...this.path, section.title]
    },
    renderContent(createElement) {
      const children = []

      if (this.section?.title) {
        children.push(createElement('h' + Math.min(Math.max(this.section.level, 1), 6), {
          class: [
            'ae-experimental-form-layout__section-header',
            'ae-form-sticky-section-header',
            {
              'ae-form-sticky-section-header--active': this.isActiveStickyHeader,
              'ae-form-sticky-section-header--static': !this.sticky,
            }
          ],
          attrs: {
            'data-ae-form-sticky-section-header': this.sticky ? '' : null,
            'data-ae-form-sticky-section-key': this.sectionKey,
            'data-ae-form-active-sticky-breadcrumb': this.isActiveStickyHeader ? '' : null,
          },
        }, this.headerPath))
      }

      if (this.shouldRenderBody) {
        const bodyChildren = []

        for (const paragraph of this.section?.paragraphs ?? []) {
          bodyChildren.push(createElement('ae-form-paragraph', {
            key: paragraph.key,
            props: {
              paragraph,
              document: this.document,
              form: this.form,
              theme: this.theme,
              readonly: this.readonly,
              itemStyle: this.paragraphItemStyle,
              fieldValidationErrors: this.fieldValidationErrors,
            },
          }))
        }

        for (const section of this.section?.subSections ?? []) {
          bodyChildren.push(createElement('ae-experimental-form-section', {
            key: section.key,
            props: {
              section,
              document: this.document,
              form: this.form,
              theme: this.theme,
              readonly: this.readonly,
              path: this.subsectionPath(section),
              sticky: this.sticky,
              activeStickySectionKey: this.activeStickySectionKey,
              fieldValidationErrors: this.fieldValidationErrors,
            },
          }))
        }

        children.push(createElement('div', {
          class: 'ae-experimental-form-layout__section-body',
          attrs: {
            'data-ae-form-section-body': '',
          },
        }, bodyChildren))
      } else {
        children.push(createElement('div', {
          class: 'ae-experimental-form-layout__section-body-placeholder',
          attrs: {
            'aria-hidden': 'true',
            'data-ae-form-section-body-placeholder': '',
          },
        }))

      }

      return createElement('section', {
        class: ['ae-form-section', 'ae-experimental-form-layout__section'],
        attrs: {
          'data-ae-form-section-key': this.sectionKey,
          'data-ae-form-section-title': this.section?.title ?? '',
          'data-ae-form-section-level': this.section?.level ?? '',
          'data-ae-form-section-layout-id': this.sectionLayoutId,
        },
      }, children)
    },
  },
  render(createElement) {
    return this.renderContent(createElement)
  },
}

export default {
  /* tslint:disable */
  render () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"scrollContainer",staticClass:"ae-experimental-form-layout ae-form-scroll-container ae-expandable-group-scope",class:{
    'ae-form-nested': _vm.nested,
    'ae-experimental-form-layout--embedded-fill': _vm.embeddedFormFill,
  },attrs:{"data-ae-form-scroll-container":""},on:{"scroll":_vm.onScroll}},[_c('div',{staticClass:"ae-experimental-form-layout__content"},[_vm._l((_vm.rootParagraphs),function(paragraph){return _c('ae-form-paragraph',{key:paragraph.key,attrs:{"paragraph":paragraph,"document":_vm.document,"form":_vm.form,"theme":_vm.theme,"readonly":_vm.readonly,"item-style":_vm.paragraphItemStyle,"field-validation-errors":_vm.fieldValidationErrors}})}),_vm._v(" "),(_vm.hasTabs)?[_c('div',{staticClass:"ae-experimental-form-layout__tab-shell",attrs:{"data-ae-form-tab-shell":""}},[_c('div',{staticClass:"ae-experimental-form-layout__tab-strip ae-scrollbars--subtle",attrs:{"role":"tablist"}},[_vm._l((_vm.tabSections),function(section){return _c('button',{key:section.key,ref:"tabButtons",refInFor:true,staticClass:"ae-experimental-form-layout__tab",class:{ 'ae-experimental-form-layout__tab--active': section.index === _vm.selectedTabIndex },attrs:{"type":"button","role":"tab","aria-selected":section.index === _vm.selectedTabIndex ? 'true' : 'false',"data-ae-form-tab-section-key":_vm.sectionStorageKey(section)},on:{"click":function($event){return _vm.selectTab(section)}}},[_vm._v(_vm._s(section.title))])}),_vm._v(" "),_c('div',{staticClass:"ae-experimental-form-layout__tab-empty",attrs:{"aria-hidden":"true"}})],2),_vm._v(" "),(_vm.selectedTab)?_c('section',{staticClass:"ae-form-section ae-experimental-form-layout__tab-panel",attrs:{"data-ae-form-active-tab-section-key":_vm.sectionStorageKey(_vm.selectedTab),"data-ae-form-section-key":_vm.sectionStorageKey(_vm.selectedTab),"data-ae-form-section-title":_vm.selectedTab.title,"data-ae-form-section-level":_vm.selectedTab.level,"data-ae-form-section-layout-id":_vm.sectionLayoutId(_vm.selectedTab)}},[_vm._l((_vm.selectedTab.paragraphs),function(paragraph){return _c('ae-form-paragraph',{key:paragraph.key,attrs:{"paragraph":paragraph,"document":_vm.document,"form":_vm.form,"theme":_vm.theme,"readonly":_vm.readonly,"item-style":_vm.paragraphItemStyle,"field-validation-errors":_vm.fieldValidationErrors}})}),_vm._v(" "),_vm._l((_vm.selectedTab.subSections),function(section){return _c('ae-experimental-form-section',{key:section.key,attrs:{"section":section,"document":_vm.document,"form":_vm.form,"theme":_vm.theme,"readonly":_vm.readonly,"path":_vm.sectionPath(_vm.selectedTab, section),"active-sticky-section-key":_vm.activeStickySectionKey,"field-validation-errors":_vm.fieldValidationErrors}})})],2):_vm._e()])]:_vm._l((_vm.rootSubSections),function(section){return _c('ae-experimental-form-section',{key:section.key,attrs:{"section":section,"document":_vm.document,"form":_vm.form,"theme":_vm.theme,"readonly":_vm.readonly,"path":[section.title],"active-sticky-section-key":_vm.activeStickySectionKey,"field-validation-errors":_vm.fieldValidationErrors}})}),_vm._v(" "),_c('div',{staticClass:"ae-experimental-form-layout__bottom-spacer",attrs:{"aria-hidden":"true"}})],2)])},
  staticRenderFns: [],
  /* tslint:enable */
  
  name: 'ae-experimental-form-layout',
  inject: ['getAeppicContext'],
  provide() {
    return {
      consumeLazyEagerSection: this.consumeLazyEagerSection,
      registerLazySection: this.registerLazySection,
      unregisterLazySection: this.unregisterLazySection,
      requestLazySectionRender: this.requestLazySectionRender,
    }
  },
  components: {
    'ae-experimental-form-section': ExperimentalFormSection,
  },
  props: {
    document: Object,
    form: Object,
    rootSection: Object,
    theme: Object,
    fieldValidationErrors: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      Aeppic: this.getAeppicContext('ae-experimental-form-layout'),
      nested: false,
      embeddedFormFill: false,
      selectedTabIndex: null,
      activeStickySectionKey: null,
      restoreScrollPending: true,
      scrollFrame: null,
      resizeFrame: null,
      idleRenderHandle: null,
      idleRenderQueue: [],
      eagerRenderedSectionCount: 0,
      eagerRenderedControlCount: 0,
      tabChangedInSession: false,
    }
  },
  computed: {
    rootParagraphs() {
      return this.rootSection?.paragraphs ?? []
    },
    rootSubSections() {
      return this.rootSection?.subSections ?? []
    },
    tabSections() {
      return this.rootSubSections.filter(section => section.level === 1 && section.type !== 'VIRTUAL')
    },
    hasTabs() {
      return this.tabSections.length > 0
    },
    selectedTab() {
      return this.tabSections.find(section => section.index === this.selectedTabIndex) ?? this.tabSections[0]
    },
    documentStorageId() {
      return this.document?.id ?? 'temporary-document'
    },
    selectedTabStorageKey() {
      return `${STORAGE_PREFIX}.selectedTab@document=${this.documentStorageId}`
    },
    paragraphItemStyle() {
      return {
        minWidth: 0,
      }
    },
  },
  mounted() {
    this.Aeppic.setContextRootElement(this.$el)
    this.updateNestingMode()
    this.updateScrollContainerHeight()
    window.addEventListener('resize', this.scheduleUpdateScrollContainerHeight)
    this.restoreSelectedTab()
    this.queueScrollRestore()
  },
  beforeDestroy() {
    this.persistScrollPosition()
    window.removeEventListener('resize', this.scheduleUpdateScrollContainerHeight)

    if (this.scrollFrame) {
      this.cancelFrame(this.scrollFrame)
    }

    if (this.resizeFrame) {
      this.cancelFrame(this.resizeFrame)
    }

    this.cancelIdleRender()

    this.Aeppic.release()
  },
  watch: {
    documentStorageId() {
      this.restoreSelectedTab()
      this.queueScrollRestore()
    },
    rootSection() {
      this.resetLazyEagerBudget()
      this.ensureSelectedTab()
      this.queueScrollRestore()
    },
    params() {
      this.updateNestingMode()
      this.scheduleUpdateScrollContainerHeight()
    },
    selectedTab() {
      this.$nextTick(() => {
        this.updateScrollContainerHeight()
        this.updateActiveStickyHeader()
      })
    },
  },
  methods: {
    truthy(value) {
      return value === true || value === 'true' || value === '1' || value === 1 || value === ''
    },
    updateNestingMode() {
      this.nested = this.isNestedForm(this.$el)
      this.embeddedFormFill = this.nested && this.truthy(
        this.params?.embeddedFormFill
          ?? this.params?.embeddedFill
          ?? this.params?.fillEmbeddedForm
      )
    },
    isNestedForm(rootElement) {
      const parent = rootElement?.parentElement
      if (!parent?.closest) {
        return false
      }

      if (parent.closest('.ae-experimental-form-layout, ._CSS_form-layout')) {
        return true
      }

      const controlBoundary = parent.closest('.ae-form-field, .ae-control, [data-ae-form-field-name], [data-ae-control-id]')
      const outerForm = parent.closest('.ae-form--layout, .ae-form, [data-ae-form-scroll-container]')
      return !!controlBoundary && !!outerForm
    },
    consumeLazyEagerSection(section) {
      if (!section || section.isVisible === false) {
        return false
      }

      if (this.eagerRenderedSectionCount >= SECTION_EAGER_RENDER_LIMIT) {
        return false
      }

      const controlCount = this.countVisibleControls(section)
      if (this.eagerRenderedSectionCount > 0 && this.eagerRenderedControlCount + controlCount > SECTION_EAGER_CONTROL_LIMIT) {
        return false
      }

      this.eagerRenderedSectionCount += 1
      this.eagerRenderedControlCount += controlCount
      return true
    },
    resetLazyEagerBudget() {
      this.eagerRenderedSectionCount = 0
      this.eagerRenderedControlCount = 0
    },
    countVisibleControls(section) {
      if (!section || section.isVisible === false) {
        return 0
      }

      let count = 0
      for (const paragraph of section.paragraphs ?? []) {
        if (paragraph?.isVisible === false) {
          continue
        }

        for (const item of paragraph.items ?? []) {
          if (item?.isVisible === false) {
            continue
          }

          if (item?.type === 'FIELD' || item?.type === 'STANDALONE_CONTROL') {
            count += 1
          }
        }
      }

      for (const child of section.subSections ?? []) {
        count += this.countVisibleControls(child)
      }

      return count
    },
    registerLazySection(sectionComponent) {
      this.requestLazySectionRender(sectionComponent)
    },
    unregisterLazySection(sectionComponent) {
      this.idleRenderQueue = this.idleRenderQueue.filter(item => item !== sectionComponent)
    },
    requestLazySectionRender(sectionComponent) {
      if (!sectionComponent || sectionComponent.bodyRendered) {
        return
      }

      if (!this.idleRenderQueue.includes(sectionComponent)) {
        this.idleRenderQueue.push(sectionComponent)
      }

      this.scheduleIdleRender()
    },
    scheduleIdleRender() {
      if (this.idleRenderHandle != null || this.idleRenderQueue.length === 0) {
        return
      }

      const callback = () => {
        this.idleRenderHandle = null
        this.renderNextIdleSection()
      }

      if (typeof requestIdleCallback === 'function') {
        this.idleRenderHandle = requestIdleCallback(callback, { timeout: SECTION_IDLE_RENDER_TIMEOUT })
      } else {
        this.idleRenderHandle = setTimeout(callback, 80)
      }
    },
    renderNextIdleSection() {
      while (this.idleRenderQueue.length > 0) {
        const sectionComponent = this.idleRenderQueue.shift()

        if (!sectionComponent || sectionComponent._isDestroyed || sectionComponent.bodyRendered) {
          continue
        }

        sectionComponent.renderBody()
        this.$nextTick(() => {
          this.updateScrollContainerHeight()
          this.updateActiveStickyHeader()
        })
        break
      }

      this.scheduleIdleRender()
    },
    cancelIdleRender() {
      if (this.idleRenderHandle == null) {
        return
      }

      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this.idleRenderHandle)
      } else {
        clearTimeout(this.idleRenderHandle)
      }

      this.idleRenderHandle = null
    },
    restoreSelectedTab() {
      const storedTabIndex = this.Aeppic.LocalStorage.getValue(this.selectedTabStorageKey)
      const storedTab = this.tabSections.find(section => section.index === storedTabIndex)
      this.selectedTabIndex = storedTab?.index ?? this.tabSections[0]?.index ?? null
    },
    ensureSelectedTab() {
      if (!this.hasTabs) {
        this.selectedTabIndex = null
        return
      }

      if (!this.selectedTab) {
        this.selectedTabIndex = this.tabSections[0].index
      }
    },
    selectTab(section) {
      if (!section || section.index === this.selectedTabIndex) {
        return
      }

      this.persistScrollPosition()
      this.resetLazyEagerBudget()
      this.selectedTabIndex = section.index
      this.tabChangedInSession = true
      this.Aeppic.LocalStorage.setValue(this.selectedTabStorageKey, section.index)

      this.$nextTick(() => {
        this.updateScrollContainerHeight()
        this.scrollToTop()
        this.updateActiveStickyHeader()
      })
    },
    sectionStorageKey(section) {
      return section?.index ?? 'root'
    },
    selectedTabScrollStorageKey() {
      return `${STORAGE_PREFIX}.scrollTop@document=${this.documentStorageId}@tab=${this.sectionStorageKey(this.selectedTab)}`
    },
    sectionPath(tab, section) {
      return [tab?.title, section?.title].filter(Boolean)
    },
    sectionLayoutId(section) {
      return this.theme?.sections?.levels?.[section?.level]?.layoutId ?? '4dfdf094-32b3-41fe-a2eb-50a809c39d7d'
    },
    scheduleUpdateScrollContainerHeight() {
      if (this.resizeFrame != null) {
        this.cancelFrame(this.resizeFrame)
      }

      this.resizeFrame = this.requestFrame(() => {
        this.resizeFrame = null
        this.updateScrollContainerHeight()
      })
    },
    updateScrollContainerHeight() {
      const scrollContainer = this.$refs.scrollContainer

      if (!scrollContainer || typeof window === 'undefined') {
        return
      }

      if (this.nested && !this.embeddedFormFill) {
        scrollContainer.style.height = 'auto'
        scrollContainer.style.maxHeight = 'none'
        return
      }

      const top = scrollContainer.getBoundingClientRect().top
      const availableHeight = Math.max(240, window.innerHeight - top)

      scrollContainer.style.height = `${availableHeight}px`
      scrollContainer.style.maxHeight = `${availableHeight}px`
    },
    queueScrollRestore() {
      this.restoreScrollPending = true

      this.$nextTick(() => {
        this.updateScrollContainerHeight()
        this.restoreScrollPosition()

        this.requestFrame(() => {
          this.updateScrollContainerHeight()
          this.restoreScrollPosition()
        })

        setTimeout(() => {
          this.updateScrollContainerHeight()
          this.restoreScrollPosition()
        }, 300)
      })
    },
    restoreScrollPosition() {
      if (!this.restoreScrollPending || this.tabChangedInSession) {
        return
      }

      const scrollContainer = this.$refs.scrollContainer

      if (!scrollContainer) {
        return
      }

      const storedScrollTop = Number(this.Aeppic.LocalStorage.getValue(this.selectedTabScrollStorageKey()) ?? 0)
      const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight

      if (storedScrollTop > 0 && maxScrollTop <= 0) {
        return
      }

      scrollContainer.scrollTop = Number.isFinite(storedScrollTop) ? storedScrollTop : 0
      this.restoreScrollPending = false
      this.updateActiveStickyHeader()
    },
    scrollToTop() {
      const scrollContainer = this.$refs.scrollContainer

      if (scrollContainer) {
        scrollContainer.scrollTop = 0
      }
    },
    onScroll() {
      if (this.scrollFrame) {
        this.cancelFrame(this.scrollFrame)
      }

      this.scrollFrame = this.requestFrame(() => {
        this.scrollFrame = null
        this.persistScrollPosition()
        this.updateActiveStickyHeader()
      })
    },
    persistScrollPosition() {
      const scrollContainer = this.$refs.scrollContainer

      if (!scrollContainer || !this.hasTabs || !this.selectedTab) {
        return
      }

      this.Aeppic.LocalStorage.setValue(this.selectedTabScrollStorageKey(), scrollContainer.scrollTop)
    },
    stickyThresholdForHeader(header) {
      const computedTop = Number.parseFloat(window.getComputedStyle(header).top)
      const stickyTop = Number.isFinite(computedTop) ? computedTop : 0

      return Math.max(0, header.offsetTop - stickyTop)
    },
    requestFrame(callback) {
      if (typeof requestAnimationFrame === 'function') {
        return requestAnimationFrame(callback)
      }

      return setTimeout(() => callback(Date.now()), 0)
    },
    cancelFrame(frame) {
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frame)
      } else {
        clearTimeout(frame)
      }
    },
    updateActiveStickyHeader() {
      const scrollContainer = this.$refs.scrollContainer

      if (!scrollContainer) {
        this.activeStickySectionKey = null
        return
      }

      const headers = Array.from(scrollContainer.querySelectorAll('[data-ae-form-sticky-section-header]')) as HTMLElement[]
      let activeHeader = null
      const scrollTop = scrollContainer.scrollTop

      for (const header of headers) {
        if (scrollTop > this.stickyThresholdForHeader(header)) {
          activeHeader = header
        } else {
          break
        }
      }

      this.activeStickySectionKey = activeHeader?.getAttribute('data-ae-form-sticky-section-key') ?? null
    },
  },
}
